import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { ApiClientError, idempotencyKeyFor } from '@/lib/http-core';
import type {
  AdminOrderActionRequest,
  AdminOrderActionResponse,
} from '@/lib/api-admin/types';
import { orderQueryKey } from './useOrderQuery';

/**
 * 무통장 입금확인 → 결제완료 전환(M-4 ④, [ID52]). (be-order 동결 계약.)
 * `POST /admin/v1/orders/{orderId}/confirm-deposit` — 입금대기(PENDING_PAYMENT)+무통장
 * 주문을 운영자가 수동으로 입금확인하면 결제완료로 전환한다. 응답 = 기존
 * AdminOrderActionResponse shape(다른 운영 액션과 동일).
 *
 * 경로가 generated OpenAPI 스펙에 미반영이라(다른 confirm-purchase 와 동일) fetch +
 * adminFetch 로 직접 호출한다. write 는 Idempotency-Key 수용. 그 외 상태 409 등은
 * BE 가 친절 메시지를 내려주고 friendlyMessage 가 토스트에 그대로 노출한다.
 */
const buildPath = (orderId: string): string =>
  `/admin/v1/orders/${orderId}/confirm-deposit`;

interface SuccessEnvelope {
  success: true;
  data: AdminOrderActionResponse;
}

interface ErrorEnvelopeShape {
  error?: { code?: string; message?: string } | null;
}

const throwError = async (res: Response, action = 'confirm-deposit'): Promise<never> => {
  try {
    const body = (await res.json()) as ErrorEnvelopeShape;
    const err = body?.error;
    if (err?.code || err?.message) {
      throw new ApiClientError(
        { code: err.code ?? 'COMMON_CONFLICT', message: err.message ?? '' },
        { status: res.status },
      );
    }
  } catch (e) {
    if (e instanceof ApiClientError) throw e;
    // JSON 파싱 실패 등은 무시하고 status 기반 일반 Error 폴백.
  }
  throw new Error(`${action} failed: ${res.status}`);
};

/** 운영자가 메모를 비웠을 때의 기본 사유 — AdminOrderActionRequest.reason 은 필수(감사 로그). */
const DEFAULT_DEPOSIT_REASON = '무통장 입금확인';

export interface ConfirmDepositInput {
  orderId: string;
  /** 입금확인 사유/메모(선택). 비우면 기본 사유로 채워 감사 로그를 보존한다. */
  reason?: string;
}

/** 입금확인과 같은 형식(사유 + Idempotency-Key)의 주문 액션 — 입금확인·취소 복원(QA-472)이 같이 쓴다. */
const useDepositActionMutation = (action: 'confirm-deposit' | 'restore-pending', defaultReason: string) => {
  const qc = useQueryClient();
  return useMutation<AdminOrderActionResponse, Error, ConfirmDepositInput>({
    mutationFn: async ({ orderId, reason }) => {
      const path = action === 'confirm-deposit' ? buildPath(orderId) : `/admin/v1/orders/${orderId}/restore-pending`;
      const trimmed = (reason ?? '').trim();
      const payload: AdminOrderActionRequest = {
        reason: trimmed === '' ? defaultReason : trimmed,
      };
      const res = await adminFetch(path, {
        method: 'POST',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('POST', path, payload),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) await throwError(res, action);
      const env = (await res.json()) as SuccessEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: async (_data, { orderId }) => {
      await qc.invalidateQueries({ queryKey: orderQueryKey(orderId) });
      await qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
  });
};

export const useConfirmDepositMutation = () => useDepositActionMutation('confirm-deposit', DEFAULT_DEPOSIT_REASON);

/**
 * QA-472 취소 복원(order #77) — 결제된 적 없는 무통장 취소 주문을 입금대기로 되돌린다(고도몰 취소복원).
 * `POST /admin/v1/orders/{orderId}/restore-pending` body { reason }. 복원 후 기존 [입금확인]으로 결제완료.
 * 409(가상계좌·결제 이력·클레임·쿠폰 사용 주문)는 BE 사유를 그대로 보인다.
 */
export const useRestorePendingMutation = () => useDepositActionMutation('restore-pending', '고객 입금 요청');
